<template>
  <div class="testimonials-wrapper" :class="{ rtl: isRTL }">
    <div class="testimonials-container">
      <!-- Header -->
      <h2 class="testimonials-title">
        <span class="title-black">{{ t('student_testimonials.heading_part1') }}</span>
        <span class="title-orange">{{ t('student_testimonials.heading_part2') }}</span>
      </h2>

      <!-- Testimonials Cards -->
      <div class="testimonials-grid">
        <div
          v-for="testimonial in testimonials"
          :key="testimonial.id"
          class="testimonial-card"
        >
          <!-- Video -->
          <div class="video-container">
            <iframe
              :src="testimonial.videoUrl"
              class="video-iframe"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>

          <!-- Testimonial Text -->
          <p class="testimonial-text">{{ t(testimonial.textKey) }}</p>
        </div>
      </div>

      <!-- CTA Button -->
      <div class="cta-container">
        <CtaButton @click="handleCTAClick" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import { useLanguage } from '@/composables/useLanguage'
import CtaButton from '../ui/CtaButton.vue';

const { t, isRTL } = useLanguage()

const props = defineProps({
  testimonials: {
    type: Array,
    required: true,
    default: () => []
  }
});

const emit = defineEmits(['ctaClick']);

const handleCTAClick = () => {
  emit('ctaClick');
};
</script>

<style scoped>
.testimonials-wrapper {
  width: 100%;
  max-width: 100vw;
  background-color: #fff;
  padding: clamp(40px, 8vw, 80px) clamp(20px, 4vw, 40px);
  box-sizing: border-box;
}

.testimonials-container {
  max-width: 1800px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(30px, 5vw, 40px);
  box-sizing: border-box;
}

.testimonials-title {
  font-size: clamp(28px, 2.5vw, 40px);
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 24px 0;
}

.title-orange {
  color: #F47D21;
}

.title-black {
  color: #212121;
}

.testimonials-grid {
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(350px, 100%), 1fr));
  gap: clamp(20px, 4vw, 30px);
  margin-bottom: clamp(30px, 5vw, 40px);
  box-sizing: border-box;
}

.testimonial-card {
  background: #fff;
  border-radius: clamp(12px, 2.5vw, 16px);
  padding: clamp(16px, 3vw, 20px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 2.5vw, 16px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  z-index: 2;
}

.play-button:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.video-duration {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.testimonial-text {
  font-size: clamp(13px, 1.1vw, 14px);
  line-height: 1.6;
  color: #333;
  margin: 0;
  min-height: 80px;
  text-align: left;
}

.rtl .testimonial-text {
  text-align: right;
  direction: rtl;
}

.rtl .testimonials-title {
  direction: rtl;
}

.student-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.student-name {
  font-size: clamp(16px, 1.3vw, 18px);
  font-weight: 600;
  color: #212121;
  margin: 0;
}

.student-level {
  font-size: clamp(12px, 1vw, 13px);
  color: #666;
  margin: 0;
}

.rating {
  display: flex;
  gap: 4px;
}

.star {
  color: #F47D21;
  font-size: 18px;
}

.cta-container {
  width: 100%;
  max-width: 700px;
  margin: clamp(15px, 3vw, 20px) auto 0;
  display: flex;
  justify-content: center;
  padding: 0 clamp(10px, 2vw, 20px);
  box-sizing: border-box;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .testimonials-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .testimonials-wrapper {
    padding: clamp(40px, 7vw, 60px) clamp(15px, 3vw, 20px);
  }

  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: clamp(16px, 3vw, 20px);
  }

  .testimonials-container {
    gap: clamp(25px, 4vw, 30px);
  }

  .testimonials-title {
    padding: 0 clamp(10px, 2vw, 15px);
  }
}

@media (max-width: 640px) {
  .testimonials-wrapper {
    padding: clamp(30px, 6vw, 50px) clamp(12px, 2.5vw, 18px);
  }
}

@media (max-width: 480px) {
  .testimonials-wrapper {
    padding: clamp(25px, 5vw, 40px) clamp(10px, 2vw, 15px);
  }

  .testimonial-card {
    padding: 14px;
  }
}

@media (max-width: 380px) {
  .testimonials-wrapper {
    padding: 20px 10px;
  }

  .testimonial-card {
    padding: 12px;
    border-radius: 10px;
  }
}
</style>
